import { useState } from 'react'
import { slugify } from '@/lib/utils'
import api from '@/lib/axios'
import type { Category } from '@/types/admin'

export function useCategoriesIndexVM(initialCategories: Category[]) {
  const [categories, setCategories] = useState<Category[]>(initialCategories)
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  const [editId, setEditId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [saving, setSaving] = useState(false)

  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handleNewNameChange = (val: string) => {
    setNewName(val)
    setNewSlug(slugify(val))
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreating(true)
    setCreateError('')
    try {
      const { data } = await api.post('/admin/categories', {
        name: newName,
        slug: newSlug,
        description: newDescription,
      })
      if (data.success) {
        setCategories((prev) => [...prev, data.category])
        setNewName('')
        setNewSlug('')
        setNewDescription('')
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { errors?: string[] } } }).response
        setCreateError(response?.data?.errors?.join(', ') ?? 'Failed to create category')
      }
    } finally {
      setCreating(false)
    }
  }

  const startEdit = (cat: Category) => {
    setEditId(cat.id)
    setEditName(cat.name)
    setEditSlug(cat.slug)
    setEditDescription(cat.description ?? '')
  }

  const cancelEdit = () => {
    setEditId(null)
  }

  const handleUpdate = async () => {
    if (!editId) return
    setSaving(true)
    try {
      const { data } = await api.patch(`/admin/categories/${editId}`, {
        name: editName, slug: editSlug, description: editDescription,
      })
      if (data.success) {
        setCategories((prev) => prev.map((c) => (c.id === editId ? data.category : c)))
        cancelEdit()
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.delete(`/admin/categories/${deleteId}`)
      setCategories((prev) => prev.filter((c) => c.id !== deleteId))
      setDeleteId(null)
    } finally {
      setDeleting(false)
    }
  }

  const isEditing = (catId: number) => editId === catId
  const deleteDialogOpen = deleteId !== null
  const createDisabled = !newName.trim()

  return {
    categories,
    newName,
    newSlug,
    newDescription,
    creating,
    createError,
    editName,
    editSlug,
    editDescription,
    saving,
    deleting,
    deleteDialogOpen,
    createDisabled,
    handleNewNameChange,
    setNewSlug,
    setNewDescription,
    handleCreate,
    startEdit,
    cancelEdit,
    handleUpdate,
    handleDelete,
    setDeleteId,
    isEditing,
    setEditName,
    setEditSlug,
    setEditDescription,
  }
}
